import React, { useCallback, useEffect, useMemo, useState } from "react";
import { FileText, ReceiptText, Send, Trash2 } from "lucide-react";
import { api, fmtMoney, formatErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";

const withLedger = (path, ledger) => path + (path.includes("?") ? "&" : "?") + `ledger=${encodeURIComponent(ledger)}`;

export const PayrollBillingSection = ({ dateFrom, dateTo, ledger, refreshKey = 0 }) => {
  const [charges, setCharges] = useState([]);
  const [payroll, setPayroll] = useState([]);
  const [busy, setBusy] = useState("");
  const [invoiceGroup, setInvoiceGroup] = useState(null);

  const load = useCallback(async () => {
    try {
      const [chargeResponse, payrollResponse] = await Promise.all([
        api.get(withLedger(`/payroll/billing?date_from=${dateFrom}&date_to=${dateTo}`, ledger)),
        api.get(withLedger(`/payroll?date_from=${dateFrom}&date_to=${dateTo}`, ledger)),
      ]);
      setCharges(chargeResponse.data || []);
      setPayroll(payrollResponse.data || []);
    } catch (error) {
      toast.error(formatErr(error.response?.data?.detail) || error.message);
    }
  }, [dateFrom, dateTo, ledger]);

  useEffect(() => { load(); }, [load, refreshKey]);

  const readyGroups = useMemo(() => {
    const groups = new Map();
    const getGroup = (colonyId, colonyName) => {
      if (!groups.has(colonyId)) groups.set(colonyId, { colony_id: colonyId, colony_name: colonyName || "Colony", payroll_ids: [], charge_ids: [], worker_names: new Set(), total: 0 });
      return groups.get(colonyId);
    };
    payroll.filter((row) => row.colony_id && !row.billing_charge_id && !row.billing_expense_id).forEach((row) => {
      const group = getGroup(row.colony_id, row.colony_name);
      group.payroll_ids.push(row.id);
      group.worker_names.add(row.worker_name);
      group.total += Number(row.colony_charge_total ?? row.gross_pay ?? 0);
    });
    charges.filter((charge) => !charge.invoiced).forEach((charge) => {
      const group = getGroup(charge.colony_id, charge.colony_name);
      group.charge_ids.push(charge.id);
      group.worker_names.add(charge.worker_name);
      group.total += Number(charge.amount || 0);
    });
    return Array.from(groups.values()).map((group) => ({ ...group, worker_names: Array.from(group.worker_names), total: Math.round(group.total * 100) / 100 }));
  }, [payroll, charges]);

  const createInvoice = async (action) => {
    if (!invoiceGroup) return;
    setBusy(invoiceGroup.colony_id);
    try {
      const response = await api.post("/mgmt/payroll-billing/invoices", {
        payroll_ids: invoiceGroup.payroll_ids,
        charge_ids: invoiceGroup.charge_ids,
        action,
      });
      const result = response.data?.invoices?.[0];
      const invoice = result?.invoice;
      if (action === "send" && result?.email_sent) toast.success(`Payroll invoice ${invoice.number} emailed to ${result.email_to}.`);
      else if (action === "send" && result?.email_error) toast.warning(`Invoice ${invoice.number} saved as a draft. ${result.email_error}`);
      else toast.success(`Payroll invoice ${invoice.number} saved as a draft.`);
      setInvoiceGroup(null);
      await load();
    } catch (error) {
      toast.error(formatErr(error.response?.data?.detail) || error.message);
    } finally {
      setBusy("");
    }
  };

  const removeCharge = async (charge) => {
    if (!window.confirm(`Remove the ${fmtMoney(charge.amount)} payroll billing charge for ${charge.colony_name || "this colony"}?`)) return;
    setBusy(charge.id);
    try {
      await api.delete(`/payroll/billing/${charge.id}`);
      toast.success("Payroll billing charge removed");
      await load();
    } catch (error) {
      toast.error(formatErr(error.response?.data?.detail) || error.message);
    } finally {
      setBusy("");
    }
  };

  return (
    <div className="space-y-7" data-testid="payroll-billing-section">
      <h1 className="management-title font-display" data-testid="payroll-billing-heading">Payroll billing</h1>
      <section aria-labelledby="ready-payroll-invoice-heading">
        <div className="management-billing-section-header flex items-end justify-between gap-3 mb-2">
          <div>
            <h2 id="ready-payroll-invoice-heading" className="management-billing-section-title font-display font-bold" data-testid="payroll-billing-ready-heading">Ready to invoice</h2>
            <p className="text-xs text-muted-foreground" data-testid="payroll-billing-ready-description">One combined payroll invoice per colony, using the same branded invoice paper and shared number sequence.</p>
          </div>
          <span className="text-xs mono-num text-muted-foreground" data-testid="payroll-billing-ready-count">{readyGroups.length} colonies</span>
        </div>
        <div className="management-billing-list border-y border-border divide-y divide-border" data-testid="payroll-billing-ready-list">
          {readyGroups.length === 0 ? (
            <p className="py-8 text-center text-sm text-muted-foreground" data-testid="payroll-billing-ready-empty">Nothing is waiting for a payroll invoice.</p>
          ) : readyGroups.map((group) => (
            <div key={group.colony_id} className="grid grid-cols-1 sm:grid-cols-12 gap-2 px-2 py-3 items-center" data-testid={`payroll-invoice-ready-${group.colony_id}`}>
              <div className="sm:col-span-3 font-semibold text-sm" data-testid={`payroll-invoice-ready-colony-${group.colony_id}`}>{group.colony_name}</div>
              <div className="sm:col-span-4 text-xs text-muted-foreground" data-testid={`payroll-invoice-ready-workers-${group.colony_id}`}>{group.worker_names.join(", ")} · {group.payroll_ids.length + group.charge_ids.length} payroll charge{group.payroll_ids.length + group.charge_ids.length === 1 ? "" : "s"}</div>
              <div className="sm:col-span-2 mono-num font-bold sm:text-right" data-testid={`payroll-invoice-ready-total-${group.colony_id}`}>{fmtMoney(group.total)}</div>
              <div className="sm:col-span-3 flex sm:justify-end">
                <Button size="sm" onClick={() => setInvoiceGroup(group)} data-testid={`payroll-invoice-review-${group.colony_id}`}><FileText className="w-3.5 h-3.5 mr-1" /> Create invoice</Button>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section aria-labelledby="payroll-billing-history-heading">
        <div className="management-billing-section-header flex items-end justify-between gap-3 mb-2">
          <div>
            <h2 id="payroll-billing-history-heading" className="management-billing-section-title font-display font-bold" data-testid="payroll-billing-history-heading">Payroll billing history</h2>
            <p className="text-xs text-muted-foreground">Separate from Expenses; invoiced charges appear under the Payroll invoice tab.</p>
          </div>
          <span className="text-xs mono-num text-muted-foreground" data-testid="payroll-billing-history-count">{charges.length} charges</span>
        </div>
        <div className="management-billing-list border-y border-border divide-y divide-border" data-testid="payroll-billing-history-list">
          {charges.length === 0 ? (
            <p className="py-8 text-center text-sm text-muted-foreground" data-testid="payroll-billing-history-empty">No payroll billing charges in this period.</p>
          ) : charges.map((charge) => (
            <div key={charge.id} className="grid grid-cols-2 sm:grid-cols-12 gap-2 px-2 py-3 items-center" data-testid={`payroll-billing-row-${charge.id}`}>
              <div className="sm:col-span-3 flex items-center gap-2 text-sm font-medium"><ReceiptText className="w-4 h-4 text-emerald-700" /> {charge.colony_name || "Colony"}</div>
              <div className="sm:col-span-2 text-sm">{charge.worker_name}</div>
              <div className="sm:col-span-3 text-xs text-muted-foreground">{charge.period_start} → {charge.period_end}</div>
              <div className="sm:col-span-2 text-right mono-num font-bold">{fmtMoney(charge.amount)}</div>
              <div className="sm:col-span-2 flex justify-end items-center gap-2">
                <span className={`text-[10px] uppercase tracking-wider font-semibold ${charge.invoiced ? "text-sky-700" : "text-amber-700"}`} data-testid={`payroll-billing-status-${charge.id}`}>{charge.invoiced ? "Invoiced" : "Awaiting invoice"}</span>
                {!charge.invoiced && <Button size="icon" variant="ghost" onClick={() => removeCharge(charge)} disabled={busy === charge.id} aria-label="Remove billing charge" data-testid={`payroll-billing-delete-${charge.id}`}><Trash2 className="w-4 h-4 text-destructive" /></Button>}
              </div>
            </div>
          ))}
        </div>
      </section>

      <Dialog open={!!invoiceGroup} onOpenChange={(value) => { if (!value) setInvoiceGroup(null); }}>
        <DialogContent data-testid="payroll-invoice-action-dialog">
          <DialogHeader>
            <DialogTitle>Payroll invoice · {invoiceGroup?.colony_name}</DialogTitle>
            <DialogDescription>Creates one combined invoice for {invoiceGroup?.worker_names?.length || 0} worker{invoiceGroup?.worker_names?.length === 1 ? "" : "s"}, numbered in the same sequence as regular invoices.</DialogDescription>
          </DialogHeader>
          <div className="flex justify-between border-y border-border py-4" data-testid="payroll-invoice-action-total"><span>Total payroll billing</span><strong className="mono-num text-lg">{fmtMoney(invoiceGroup?.total || 0)}</strong></div>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => createInvoice("draft")} disabled={!!busy} data-testid="payroll-invoice-save-draft"><FileText className="w-4 h-4 mr-2" /> Save as draft</Button>
            <Button onClick={() => createInvoice("send")} disabled={!!busy} data-testid="payroll-invoice-create-send"><Send className="w-4 h-4 mr-2" /> Create &amp; email</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};